import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, CircularProgress, Fade, Typography } from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";

export const CALCULATE_PROMPT = "Click Calculate to display the report";
export const CALCULATING_MESSAGES = [
  "Calculating report…",
  "Gathering the data…",
  "Crunching numbers…",
  "Adding the finishing touches…",
  "Cleaning up…",
];
export const CALCULATING_MESSAGE_INTERVAL_MS = 5000;

// The fill's floor, however small the window. The box still grows past it
// when its content needs more -- a message wrapped onto two lines.
const MIN_HEIGHT = 120;

/**
 * The box's min height that ends the page at the window's bottom edge
 * (task 5111):
 * the window's height, less everything above the box and below it. Re-measured
 * whenever the window or the page around the box resizes -- the filters above
 * it grow and shrink as chips fold and periods are picked.
 */
const useFillWindowHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return undefined;
    const fit = () => {
      const boxRect = box.getBoundingClientRect();
      const above = boxRect.top + window.scrollY;
      const below =
        document.body.getBoundingClientRect().bottom - boxRect.bottom;
      const windowHeight = document.documentElement.clientHeight;
      const fill = Math.floor(windowHeight - above - below);
      setHeight(Math.max(MIN_HEIGHT, fill));
    };
    fit();
    window.addEventListener("resize", fit);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("resize", fit);
      observer?.disconnect();
    };
  }, []);

  return { ref, height };
};

/**
 * The calculating message to show (task 8269): advances one message every
 * interval while a run is in flight, then holds on the last one until the
 * run lands. Each new run starts again from the first.
 */
const useCalculatingMessage = (isCalculating: boolean) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    if (!isCalculating) return undefined;
    const timer = window.setInterval(
      () =>
        setIndex((current) =>
          Math.min(current + 1, CALCULATING_MESSAGES.length - 1),
        ),
      CALCULATING_MESSAGE_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [isCalculating]);

  return { index, message: CALCULATING_MESSAGES[index] };
};

/**
 * What stands in for the report below the action row until a run for the
 * inputs on screen has landed (task 5001): the figures, charts and cards are
 * hidden rather than shown as placeholders a reader could mistake for the
 * client's numbers. While that run is in flight it says so instead. It fills
 * the rest of the window rather than pushing the page into a scroll.
 */
const CalculatePrompt = ({ isCalculating }: { isCalculating: boolean }) => {
  const { ref, height } = useFillWindowHeight();
  const calculating = useCalculatingMessage(isCalculating);
  return (
    <Box
      ref={ref}
      role="status"
      sx={{
        mt: 3,
        minHeight: height ?? MIN_HEIGHT,
        boxSizing: "border-box",
        py: 2,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
        border: "2px dashed",
        borderColor: "primary.light",
        borderRadius: 2,
        backgroundColor: "#F7FCFF",
        color: "#1B4F72",
      }}
    >
      {isCalculating ? (
        <CircularProgress aria-hidden size={48} />
      ) : (
        <CalculateOutlinedIcon aria-hidden sx={{ fontSize: 56 }} />
      )}
      {isCalculating ? (
        // Keyed by the message, so each one remounts and fades in.
        <Fade key={calculating.index} in timeout={300}>
          <Typography variant="h5" component="p" sx={{ fontWeight: "bold" }}>
            {calculating.message}
          </Typography>
        </Fade>
      ) : (
        <Typography variant="h5" component="p" sx={{ fontWeight: "bold" }}>
          {CALCULATE_PROMPT}
        </Typography>
      )}
    </Box>
  );
};

export default CalculatePrompt;
